import { type CSSProperties, type ReactNode, useMemo } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';

import {
  type RecentlyDeletedItemAsset,
  type RecentlyDeletedItemBoard,
  type RecentlyDeletedItemCustomField,
  type RecentlyDeletedItemLibrary,
  type RecentlyDeletedTableItem,
} from '~/components/RecentlyDeleted/types';
import { getLoaderRow, MOBILE_TABLE_ROW_HEIGHT, TABLE_ROW_HEIGHT } from '~/components/TableView/tableRowUtils';
import { type AirTableRow } from '~/components/TableView/types';

export type useRecentlyDeletedTableRowsParams = {
  data: RecentlyDeletedTableItem[];
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isInitialLoading: boolean;
  hasNextPage?: boolean;
  renderAssetRow: (params: {
    rowStyle: CSSProperties;
    key: string | number;
    data: RecentlyDeletedItemAsset;
  }) => ReactNode;
  renderBoardRow: (params: {
    rowStyle: CSSProperties;
    key: string | number;
    data: RecentlyDeletedItemBoard;
  }) => ReactNode;
  renderLibraryRow: (params: {
    rowStyle: CSSProperties;
    key: string | number;
    data: RecentlyDeletedItemLibrary;
  }) => ReactNode;
  renderCustomFieldRow: (params: {
    rowStyle: CSSProperties;
    key: string | number;
    data: RecentlyDeletedItemCustomField;
  }) => ReactNode;
};

export const useRecentlyDeletedTableRows = ({
  data = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isInitialLoading,
  renderAssetRow,
  renderBoardRow,
  renderLibraryRow,
  renderCustomFieldRow,
}: useRecentlyDeletedTableRowsParams) => {
  const { isAboveMediumScreen } = useBreakpointsContext();

  const rows = useMemo(() => {
    const tableRows: AirTableRow[] = [];

    data.forEach((rowData) => {
      switch (rowData.type) {
        case 'asset':
          tableRows.push({
            height: !isAboveMediumScreen ? MOBILE_TABLE_ROW_HEIGHT : TABLE_ROW_HEIGHT,
            render: (rowStyle, key) => renderAssetRow?.({ rowStyle, key, data: rowData.item }),
          });
          break;
        case 'board':
          tableRows.push({
            height: !isAboveMediumScreen ? MOBILE_TABLE_ROW_HEIGHT : TABLE_ROW_HEIGHT,
            render: (rowStyle, key) => renderBoardRow?.({ rowStyle, key, data: rowData.item }),
          });
          break;
        case 'library':
          tableRows.push({
            height: !isAboveMediumScreen ? MOBILE_TABLE_ROW_HEIGHT : TABLE_ROW_HEIGHT,
            render: (rowStyle, key) => renderLibraryRow?.({ rowStyle, key, data: rowData.item }),
          });
          break;
        case 'customField':
          tableRows.push({
            height: !isAboveMediumScreen ? MOBILE_TABLE_ROW_HEIGHT : TABLE_ROW_HEIGHT,
            render: (rowStyle, key) => renderCustomFieldRow?.({ rowStyle, key, data: rowData.item }),
          });
          break;
      }
    });

    if (hasNextPage || isFetchingNextPage || isInitialLoading) {
      tableRows.push(getLoaderRow(isInitialLoading, fetchNextPage));
    }

    return tableRows;
  }, [
    data,
    fetchNextPage,
    hasNextPage,
    isAboveMediumScreen,
    isFetchingNextPage,
    isInitialLoading,
    renderAssetRow,
    renderBoardRow,
    renderCustomFieldRow,
    renderLibraryRow,
  ]);

  return rows;
};
